import React, { useState, useEffect } from "react";
import { TennisScoreEngine } from "./TennisScoreEngine";
import ScoreInitModal from "./ScoreInitModal";
import PointForm from "./PointForm";
import { refZone, refSousType } from "./data/refs";
import { PointCodeGenerator } from "./PointCodeGenerator";
// import { ExcelExporter } from "./ExcelExporter";  // 🔇 Désactivé temporairement
import { getPlayerDisplayName } from './playerDisplay';
import LiveScoreDisplay from './LiveScoreDisplay';
import { markMatchAsModified, finishMatch } from './services/syncService';
import { supabase } from './supabaseClient';  // 🆕 Pour le broadcast
import ChatPanel from './ChatPanel';  // 🆕 Chat en temps réel

function MatchLivePage({ currentUser, match, onBack, onTestZones, onScoreUpdate }) {
  // ÉTAT GLOBAL - MOTEUR PERSISTANT
  const [scoreEngine, setScoreEngine] = useState(null);
  const [showScoreInitModal, setShowScoreInitModal] = useState(false);
  const [currentScore, setCurrentScore] = useState({ 
    jeuScore: "0-0", 
    sets: [[0, 0]], 
    server: match.premierServeur || "J1",
    tieBreakActive: false,
    superTieBreakActive: false,
    totalPoints: 0 
  });
  const [pointForm, setPointForm] = useState({
    familleCode: "",
    sousTypeCode: "",
    nbFrappesCode: "",
    resultatCode: null,  // 👈 null = aucun bouton actif (évite "" = "Point" actif)
    serviceReussite: "",
    zoneCode: "",
    effetCode: "",
    fauteDirecteCode: "",
  });
  const [pointsCount, setPointsCount] = useState(0);

  const [matchComments, setMatchComments] = useState("");

  // eslint-disable-next-line no-unused-vars
  const [resetKey, setResetKey] = useState(0);
  
  // 🆕 État pour le broadcast
  const [broadcastChannel, setBroadcastChannel] = useState(null);
  
  // 🆕 État pour éviter la double-synchro
  const [autoSyncTriggered, setAutoSyncTriggered] = useState(false);
  
  // 🆕 État pour le modal de fin de match
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [finishReason, setFinishReason] = useState('normal'); // 'normal' ou 'abandon'
  const [manualWinner, setManualWinner] = useState(null); // 'J1' ou 'J2'

  // 🆕 DÉTECTION AUTOMATIQUE DE FIN DE MATCH
  useEffect(() => {
    // Vérifier si le match vient de se terminer
    if (currentScore?.matchFinished && 
        match.status !== 'FINISHED' && 
        !autoSyncTriggered) {
      
      console.log('🏁 Match terminé détecté ! Synchronisation automatique...');
      setAutoSyncTriggered(true);  // Éviter les doubles déclenchements
      
      // Appeler finishMatch automatiquement
      finishMatch(match.id || match.matchId)
        .then(result => {
          console.log('✅ Match finalisé et synchronisé automatiquement', result);
          
          // Optionnel : Afficher une notification
          if (result.success) {
            // Vous pouvez ajouter un modal ici si vous voulez
            console.log('🎉 Match terminé et points synchronisés dans Supabase !');
          }
        })
        .catch(error => {
          console.error('❌ Erreur auto-sync:', error);
          setAutoSyncTriggered(false);  // Permettre un nouvel essai en cas d'erreur
        });
    }
  }, [currentScore?.matchFinished, match.status, match.id, match.matchId, autoSyncTriggered]);

  const isPointValid = React.useMemo(() => {
    // 🆕 Mode point libre → valide dès que "?" est sélectionné
    if (pointForm.familleCode === '?') return true;
    // 1️⃣ Service mode (Plus) → valide si service+zone+résultat
    const serviceMode = pointForm.serviceReussite && pointForm.zoneCode && pointForm.resultatCode;
    if (serviceMode) {
      return true;  // Résultat active POINT (famille/sousType auto)
    }
    // 2️⃣ Mode normal → famille + résultat
    return PointCodeGenerator.isValidMainCode(pointForm);
  }, [pointForm]);
  
    useEffect(() => {
      console.log("🔄 Initialisation TennisScoreEngine pour match", match.matchId);

      const matchFormats = [
        match.formatSet1Code || "STD",
        match.formatSet2Code || "STD", 
        match.formatSet3Code || "STD",
        match.formatSet4Code || "NON",
        match.formatSet5Code || "NON",
      ];

      const engine = new TennisScoreEngine({
        matchFormats,
        formatMatch: match.formatMatch || "BEST_OF_3",
        premierServeur: match.premierServeur || "J1"
      });

      // 🆕 Utiliser la même clé partout
      const storageKey = `match_${match.id || match.matchId}`;
      const saved = localStorage.getItem(storageKey);
      const hasSavedData = !!saved;  // 👈 true/false

      if (hasSavedData) {
        const data = JSON.parse(saved);
        console.log("💾 Match restauré:", data.currentScore.jeuScore);
        
        // 🔧 PROTECTION: Vérifier que engineState existe et est valide
        if (data.engineState && data.engineState.sets !== undefined) {
          engine.restoreState(data.engineState);
          // 🆕 Après restauration, forcer le server avec currentScore.server
          // (peut différer si premierServeur a été modifié en édition)
          if (data.currentScore?.server && data.currentScore.server !== engine.server) {
            console.log(`🎾 CORRECTION server: engine=${engine.server} → localStorage=${data.currentScore.server}`);
            engine.server = data.currentScore.server;
          }
        } else {
          console.warn('⚠️ engineState manquant ou invalide, initialisation par défaut');
        }
        
        setCurrentScore(data.currentScore);
        
        // 🆕 Assurer que pointForm a une valeur par défaut si absent
        setPointForm(data.pointForm || {
          familleCode: "",
          sousTypeCode: "",
          nbFrappesCode: "",
          resultatCode: null,
          serviceReussite: "",
          zoneCode: "",
          effetCode: "",
          fauteDirecteCode: "",
        });
        
        setPointsCount(data.pointsCount || 0);
        setMatchComments(data.matchComments || "");
        
        // 🆕 Notifier App.js du score restauré
        if (onScoreUpdate) {
          onScoreUpdate(data.currentScore);
        }
      } 

      setScoreEngine(engine);

      // ✅ FORCE SERVEUR UNIQUEMENT POUR LES NOUVEAUX MATCHS (pas restaurés)
      if (!hasSavedData) {
        setTimeout(() => {  // 👈 après rendu
          const score = engine.getCurrentScore();
          const serveur = match.premierServeur || "J1";
          score.server = serveur;
          setCurrentScore(score);
          
          // 🆕 Notifier App.js du score initial
          if (onScoreUpdate) {
            onScoreUpdate(score);
          }
          
          console.log("🎾 SERVEUR forcé pour nouveau match:", serveur);
        }, 0);
      } else {
        // 🆕 Pour les matchs restaurés, notifier immédiatement avec le serveur restauré
        console.log("🎾 SERVEUR restauré depuis localStorage:", engine.server);
      }
    }, [match.id, match.matchId, match.formatMatch, match.formatSet1Code, match.formatSet2Code, match.formatSet3Code, match.formatSet4Code, match.formatSet5Code, match.premierServeur, onScoreUpdate]);

      // 2. useEffect SAUVEGARDE AUTO (CORRIGÉ)
      useEffect(() => {
        if (scoreEngine) {
          const interval = setInterval(() => {
            updateScore();
          }, 30000); // 30s
          
          return () => clearInterval(interval);
        }
      }, [scoreEngine]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 Initialiser le channel de broadcast Supabase Realtime
  useEffect(() => {
    if (!match.id) return;
    
    console.log('📡 Initialisation du channel broadcast pour match:', match.id);
    
    const channel = supabase.channel(`match:${match.id}`, {
      config: {
        broadcast: { self: false }  // Ne pas recevoir ses propres messages
      }
    });
    
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('📡 Channel broadcast actif:', match.id);
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ Erreur channel broadcast');
      }
    });
    
    setBroadcastChannel(channel);
    
    return () => {
      console.log('📡 Fermeture du channel broadcast');
      supabase.removeChannel(channel);
    };
  }, [match.id]);

      const updateScore = (shouldBroadcast = false) => {
        if (!scoreEngine) return;
        const score = scoreEngine.getCurrentScore();
        console.log("📊 UPDATE:", score.jeuScore, score.sets); 
        setCurrentScore(score);
        setPointsCount(scoreEngine.points.length);

        // 🆕 Notifier App.js du changement de score
        if (onScoreUpdate) {
          onScoreUpdate(score);
        }

        // ✅ SAUVEGARDE FORCÉE CORRIGÉE
        // 🆕 Utiliser la même clé pour charger et sauvegarder
        const storageKey = `match_${match.id || match.matchId}`;
        
        const savedMatchStr = localStorage.getItem(storageKey);
        const savedMatch = savedMatchStr ? JSON.parse(savedMatchStr) : {};
        
        // 🔍 DEBUG: Vérifier l'état avant sauvegarde
        const engineState = scoreEngine.saveState();
        console.log('🔍 DEBUG SAUVEGARDE:');
        console.log('  scoreEngine.points.length:', scoreEngine.points.length);
        console.log('  engineState.points.length:', engineState.points.length);
        console.log('  scoreEngine.points:', scoreEngine.points);
        console.log('  engineState.points:', engineState.points);
        
        localStorage.setItem(storageKey, JSON.stringify({
          ...savedMatch,
          id: match.id,  // 🆕 Garder l'UUID
          matchId: match.matchId,
          currentScore: score, 
          pointForm, 
          pointsCount: scoreEngine.points.length,
          engineState: engineState,
          matchComments: matchComments
        }));
        
        console.log('💾 SAUVEGARDÉ clé:', storageKey, 'points:', scoreEngine.points.length);
        
        // 🆕 Marquer comme modifié pour la synchro
        markMatchAsModified(match.id || match.matchId);
        
        // 🆕 Broadcaster le score en temps réel SEULEMENT si nouveau point
        if (shouldBroadcast && broadcastChannel && match.id && scoreEngine) {
          try {
            // Essayer de générer le code du dernier point
            let lastPointCode = null;
            let lastPointWinner = null;
            let lastPoint = null;  // 🆕 Déclarer ici pour être accessible dans broadcast
            
            if (scoreEngine.points.length > 0) {
              lastPoint = scoreEngine.points[scoreEngine.points.length - 1];  // 🆕 Assigner ici
              
              // Récupérer le winner
              lastPointWinner = lastPoint.winner;
              
              // ✅ Utiliser le codePoint stocké
              if (lastPoint.codePoint) {
                // Format: "CL5;1;2;;" 
                // Parties: [0]="CL5", [1]="1", [2]="2", [3]="", [4]=""
                const parts = lastPoint.codePoint.split(';');
                const mainCode = parts[0];  // "CL5"
                
                // Le code principal peut contenir:
                // - Famille (1 char): C, S, R, V, T
                // - Sous-type (1+ chars): L, 2, C, etc.
                // - Résultat (0-1 char): '' (blanc=gagnant) ou 'F' (faute)
                // - NbFrappes (0-1 char): 3, 5, 9
                
                // Pour le translator, on envoie le code TEL QUEL
                // car il gère déjà le blanc comme gagnant
                lastPointCode = mainCode;  // "CL5" tel quel
                
                console.log('✅ Code:', lastPointCode, '- Winner:', lastPointWinner);
              } else {
                console.warn('⚠️ Pas de code point stocké');
              }
            }
            
            broadcastChannel.send({
              type: 'broadcast',
              event: 'score_update',
              payload: {
                score: score,
                lastPointCode: lastPointCode,
                lastPointWinner: lastPointWinner,
                lastPointFull: lastPoint,  // Point complet pour analyse de contexte
                scoreEngineData: {  // 🆕 Données du ScoreEngine pour analyse complète
                  sets: scoreEngine.sets,
                  formatMatch: scoreEngine.formatMatch,
                  lastTieBreakScores: scoreEngine.lastTieBreakScores,
                  points: scoreEngine.points  // 🆕 Pour l'historique spectateur
                },
                pointsCount: scoreEngine.points.length,
                timestamp: new Date().toISOString()
              }
            });
            
            console.log('📡 Score broadcasted:', score.jeuScore, '- Point:', lastPointCode, '- Winner:', lastPointWinner);
          } catch (err) {
            console.error('⚠️ Erreur broadcast (ignorée):', err.message);
            // Ne pas bloquer l'app si le broadcast échoue
          }
        }
      };

    // ⚡ Initialiser le moteur depuis un score en cours
    const handleScoreInit = (config) => {
      if (!scoreEngine) return;
      // Injecter les scores TB des sets terminés dans lastTieBreakScores
      if (config.setsTb) {
        config.lastTieBreakScores = config.setsTb;
      }
      scoreEngine.initializeFromScore(config);
      const score = scoreEngine.getCurrentScore();
      setCurrentScore(score);
      setShowScoreInitModal(false);

      // Sauvegarder l'état initialisé
      const storageKey = `match_${match.id || match.matchId}`;
      const engineState = scoreEngine.saveState();
      const savedData = {
        currentScore: score,
        engineState,
        pointsCount: 0,
        pointForm: {
          familleCode: "", sousTypeCode: "", nbFrappesCode: "",
          resultatCode: null, serviceReussite: "", zoneCode: "",
          effetCode: "", fauteDirecteCode: "",
        },
        matchComments: "",
      };
      localStorage.setItem(storageKey, JSON.stringify(savedData));
      console.log('⚡ Score initialisé et sauvegardé:', score.jeuScore);
    };

    const addPointForPlayer = (player) => {
      if (!scoreEngine) return;
      
      const pointFormToSave = { ...pointForm };
      
      // 👇 NOUVEAU : SERVICE MODE → auto famille "S" + sousType = zone
      if (pointFormToSave.serviceReussite && pointFormToSave.zoneCode && pointFormToSave.resultatCode && !pointFormToSave.familleCode) {
        pointFormToSave.familleCode = "S";
        pointFormToSave.sousTypeCode = pointFormToSave.zoneCode;  // Zone 1→S1, 2→S2, 3→S3
      }
      
      // 👇 NOUVEAU : validation service OU normale
      const serviceMode = pointFormToSave.serviceReussite && pointFormToSave.zoneCode && pointFormToSave.resultatCode;
      const isPointLibre = pointFormToSave.familleCode === '?';  // 🆕
      if (!serviceMode && !isPointLibre && !PointCodeGenerator.isValidMainCode(pointFormToSave)) {
        alert("⚠️ **Obligatoire** : Famille + Résultat\n\n**OU** Plus → Service + Zone + Résultat");
        return;
      }
      
      const pointCode = PointCodeGenerator.generateCode(pointFormToSave);
      console.log("🎾 POINT FINAL", { joueur: player, codePoint: pointCode, form: pointFormToSave });
      
      scoreEngine.addPoint(player, pointFormToSave, pointCode);  // 🆕 Passer le code
      updateScore(true);  // 🆕 shouldBroadcast = true (nouveau point)
      
      // 👇 RESET COMPLET (y compris service/zone)
      setPointForm({
        familleCode: "",
        sousTypeCode: "",
        nbFrappesCode: "",
        resultatCode: null,  // 👈 null = aucun bouton actif
        serviceReussite: "",
        zoneCode: "",
        effetCode: "",
        fauteDirecteCode: ""
      });
    };

    const undoPoint = () => {
        if (!scoreEngine || scoreEngine.points.length === 0) return;
        
        // 👇 RÉCUPÉRER le dernier point AVANT de l'annuler
        const lastPoint = scoreEngine.points[scoreEngine.points.length - 1];
        console.log("🔙 UNDO - Dernier point:", lastPoint);
        
        // 👇 CHARGER le formulaire avec les données du point annulé
        if (lastPoint && lastPoint.pointData) {
          setPointForm({
            familleCode: lastPoint.pointData.familleCode || "",
            sousTypeCode: lastPoint.pointData.sousTypeCode || "",
            nbFrappesCode: lastPoint.pointData.nbFrappesCode || "",
            resultatCode: lastPoint.pointData.resultatCode || "",
            serviceReussite: lastPoint.pointData.serviceReussite || "",
            zoneCode: lastPoint.pointData.zoneCode || "",
            effetCode: lastPoint.pointData.effetCode || "",
            fauteDirecteCode: lastPoint.pointData.fauteDirecteCode || ""
          });
          console.log("📝 Formulaire chargé avec:", lastPoint.pointData);
        }
        
        // 👇 PUIS annuler le point
        scoreEngine.undoLastPoint();
        
        // 🆕 Mettre à jour le score local
        const score = scoreEngine.getCurrentScore();
        setCurrentScore(score);
        setPointsCount(scoreEngine.points.length);
        
        // 🆕 Sauvegarder
        const storageKey = `match_${match.id || match.matchId}`;
        const savedMatchStr = localStorage.getItem(storageKey);
        const savedMatch = savedMatchStr ? JSON.parse(savedMatchStr) : {};
        
        localStorage.setItem(storageKey, JSON.stringify({
          ...savedMatch,
          id: match.id,
          matchId: match.matchId,
          currentScore: score, 
          pointForm, 
          pointsCount: scoreEngine.points.length,
          engineState: scoreEngine.saveState(),
          matchComments: matchComments
        }));
        
        console.log('💾 SAUVEGARDÉ après undo clé:', storageKey, 'points:', scoreEngine.points.length);
        
        // 🆕 Marquer comme modifié
        markMatchAsModified(match.id || match.matchId);
        
        // 🆕 BROADCASTER l'annulation aux spectateurs
        if (broadcastChannel && match.id) {
          try {
            broadcastChannel.send({
              type: 'broadcast',
              event: 'point_undo',  // 🆕 Type spécial pour annulation
              payload: {
                score: score,
                scoreEngineData: {
                  sets: scoreEngine.sets,
                  formatMatch: scoreEngine.formatMatch,
                  lastTieBreakScores: scoreEngine.lastTieBreakScores
                },
                pointsCount: scoreEngine.points.length,
                timestamp: new Date().toISOString()
              }
            });
            
            console.log('🔙 Annulation broadcastée aux spectateurs');
          } catch (err) {
            console.error('⚠️ Erreur broadcast undo:', err.message);
          }
        }
    };


  // Chargement en cours
  if (!scoreEngine) {
    return (
      <div style={{ padding: "24px", textAlign: "center", fontFamily: "Arial, sans-serif" }}>
        <div>Chargement du match...</div>
      </div>
    );
  }

  // 🆕 FONCTION : Ouvrir le modal de fin de match
  const handleFinishMatch = () => {
    const isMatchFinished = currentScore?.matchFinished;
    
    if (isMatchFinished) {
      // Match déjà terminé → Juste forcer la synchro
      if (window.confirm(
        '🏁 Forcer la synchronisation ?\n\n' +
        'Le match est déjà terminé.\n' +
        'Cette action va forcer la synchronisation des points.\n\n' +
        `${pointsCount} points seront synchronisés.`
      )) {
        confirmFinishMatch('normal', null);
      }
    } else {
      // Match en cours → Ouvrir le modal
      setFinishReason('normal');
      setManualWinner(null);
      setShowFinishModal(true);
    }
  };
  
  // 🆕 FONCTION : Confirmer la fin du match
  const confirmFinishMatch = async (reason, winner) => {
    setShowFinishModal(false);
    
    try {
      console.log('🏁 Finalisation du match...', { reason, winner });
      
      // 🆕 Si abandon, sauvegarder le winner manuel dans le match
      if (reason === 'abandon' && winner) {
        const storageKey = `match_${match.id || match.matchId}`;
        const savedData = localStorage.getItem(storageKey);
        
        if (savedData) {
          const matchData = JSON.parse(savedData);
          
          // Mettre à jour currentScore avec le winner manuel
          matchData.currentScore = {
            ...matchData.currentScore,
            winner: winner,
            abandonWinner: winner, // Marqueur spécial pour savoir que c'est un abandon
            matchFinished: true
          };
          
          // Mettre à jour aussi engineState
          if (matchData.engineState) {
            matchData.engineState.abandonWinner = winner;
          }
          
          localStorage.setItem(storageKey, JSON.stringify(matchData));
          
          // Mettre à jour aussi dans la liste des matchs
          const matches = JSON.parse(localStorage.getItem('matches') || '[]');
          const index = matches.findIndex(m => m.id === match.id);
          if (index !== -1) {
            matches[index].currentScore = matchData.currentScore;
            localStorage.setItem('matches', JSON.stringify(matches));
          }
          
          console.log(`✅ Winner manuel sauvegardé: ${winner}`);
        }
      }
      
      const result = await finishMatch(match.id || match.matchId);
      
      if (result.success) {
        alert(
          '✅ Match terminé !\n\n' +
          (reason === 'abandon' ? `Victoire par abandon: ${getPlayerDisplayName(match, winner)}\n` : '') +
          `${pointsCount} points synchronisés\n` +
          'Statistiques calculées'
        );
        
        onBack();  // Retour à la liste des matchs
      } else {
        alert('❌ Erreur lors de la finalisation :\n\n' + result.error);
      }
    } catch (error) {
      console.error('❌ Erreur finalisation:', error);
      alert('❌ Erreur lors de la finalisation du match');
    }
  };

  // 🆕 FONCTION : Générer et partager le lien spectateur (compatible ngrok)
  const handleShare = async () => {
    const link = `${window.location.origin}/spectator/${match.id}`;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isNgrok = window.location.hostname.includes('ngrok');

    // 🆕 Marquer le match comme partagé dans Supabase
    try {
      await supabase
        .from('matches')
        .update({ isShared: true })
        .eq('id', match.id);
      console.log('✅ Match marqué comme partagé (isShared = true)');
    } catch (err) {
      console.warn('⚠️ Impossible de marquer le match comme partagé:', err.message);
      // Ne pas bloquer le partage si la mise à jour échoue
    }

    try {
      await navigator.clipboard.writeText(link);
      
      let message = `✅ Lien copié dans le presse-papier !\n\n${link}\n\n📱 Partagez ce lien pour que vos spectateurs suivent le match en direct !\n\nLe score se mettra à jour automatiquement après chaque point.`;
      
      if (isLocalhost && !isNgrok) {
        message += '\n\n⚠️ ATTENTION : Ce lien (localhost) fonctionne uniquement sur votre PC.\n\n';
        message += 'Pour partager avec d\'autres appareils :\n';
        message += '1. Utilisez ngrok : ngrok http 3000\n';
        message += '2. Ou déployez sur Vercel pour une URL permanente';
      }
      
      if (isNgrok) {
        message += '\n\n✅ URL ngrok détectée - Ce lien fonctionne sur tous les appareils connectés à Internet !';
      }
      
      alert(message);
    } catch (err) {
      console.error('Clipboard error:', err);
      
      let promptMessage = '📋 Copiez ce lien pour partager le match :\n\n(Appuyez sur Ctrl+C ou Cmd+C puis OK)';
      
      if (isLocalhost && !isNgrok) {
        promptMessage = '⚠️ localhost ne fonctionne que sur ce PC\n\nUtilisez ngrok ou Vercel pour partager\n\nCopiez ce lien :';
      }
      
      const userCopied = prompt(promptMessage, link);
      
      if (userCopied) {
        alert('✅ Partagez ce lien avec vos spectateurs !');
      }
    }
  };


  // 5. Formats du match (déjà calculés dans useEffect)
const matchFormats = [
  match.formatSet1Code || "STD",
  match.formatSet2Code || "STD", 
  match.formatSet3Code || "STD",
  match.formatSet4Code || "NON",
  match.formatSet5Code || "NON",
];

// MatchLivePage.jsx - AJOUT NOUVEAU

// 1. Les 6 scores "égalité" 
const EQUALITY_SCORES = [
  ["0", "0"], ["15", "15"], ["30", "30"], ["40", "40"], 
  ["30", "0"], ["40", "15"],["0", "30"],["15", "40"]
];

// 2. Parser le score jeu
function parseJeuScore(jeuScore) {
  const [j1, j2] = jeuScore.split("-").map(s => s.trim());
  return { j1, j2 };
}

// 3. Est-ce un score "égalité" ?
function isEqualitySide({ j1, j2 }) {
  console.log("🔍 isEqualitySide", { j1, j2, scores: EQUALITY_SCORES });
  return EQUALITY_SCORES.some(([a, b]) => a === j1 && b === j2);
}

// 4. Set courant No-Ad ?
function isNoAdCurrentSet(matchFormats, currentSetIndex = 0) {
  const code = matchFormats[currentSetIndex] || "STD";
  return code === "NOAD" || code === "NOADT10";
}

// 6. Sous-types dynamiques S/T
function getDynamicSousTypes(familleCode, jeuScore) {
  if (familleCode !== "S" && familleCode !== "T") return refSousType[familleCode] || [];
  
  const { j1, j2 } = parseJeuScore(jeuScore);
  const currentSetIndex = Math.max(
  0, 
  currentScore.sets?.reduce((maxIdx, set, idx) => 
    set && (set[0] > 0 || set[1] > 0) ? Math.max(maxIdx, idx) : maxIdx, -1
  )
  );
  const noAd = isNoAdCurrentSet(matchFormats, currentSetIndex);
  
  // Exception No-Ad 40/40 → 6 options
  if (noAd && j1 === "40" && j2 === "40") return refSousType[familleCode] || [];
  
  // Égalité → 1,2,3 | Avantage → 4,5,6
  const equalitySide = isEqualitySide({ j1, j2 });
  const codes = equalitySide ? ["1", "2", "3"] : ["4", "5", "6"];
  
  return (refSousType[familleCode] || []).filter(opt => codes.includes(opt.code));
}


  // 🔁 6bis. Remap des sous-types dynamiques → { code, labelFr }
  const rawDynamicSousTypes = getDynamicSousTypes(
    pointForm.familleCode,
    currentScore.jeuScore
  );

  const dynamicSousTypes = rawDynamicSousTypes.map((st) => ({
    code: st.code,
    // on accepte plusieurs formats d’origine
    labelFr: st.labelFr || st.label || st.libelle,
  }));


// 7. Zones dynamiques (codes 1-3 ou 4-6)
const dynamicZoneCodes = (() => {
  const { j1, j2 } = parseJeuScore(currentScore.jeuScore);
  const currentSetIndex = Math.max(
    0, 
    currentScore.sets?.reduce((maxIdx, set, idx) => 
      set && (set[0] > 0 || set[1] > 0) ? Math.max(maxIdx, idx) : maxIdx, -1
    )
  );
  const noAd = isNoAdCurrentSet(matchFormats, currentSetIndex);

  if (noAd && j1 === "40" && j2 === "40") {
    // No-Ad à 40/40 → toutes les 6 zones possibles
    return ["1", "2", "3", "4", "5", "6"];
  }

  const equalitySide = isEqualitySide({ j1, j2 });
  // Égalité → 1,2,3 | Avantage → 4,5,6
  return equalitySide ? ["1", "2", "3"] : ["4", "5", "6"];
})();

// 7bis. Remap zones → { code, labelFr } depuis refZone
const dynamicZonesMapped = dynamicZoneCodes
  .map((code) => refZone.find((z) => z.code === code))
  .filter(Boolean)                          // on enlève les undefined au cas où
  .map((z) => ({
    code: z.code,
    labelFr: z.labelFr,                     // label fr officiel
  }));


  return (
    <div style={{ 
      padding: "16px", 
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
      minHeight: "100vh",
      backgroundColor: "#f8f9fa",
      position: "relative"
    }}>

      {/* SCORE LIVE */}
      <LiveScoreDisplay match={match} currentScore={currentScore} points={scoreEngine?.points || []} onScoreInit={() => setShowScoreInitModal(true)} />

      {/* FORMULAIRE POINT (type Excel, tactile) */}
      <PointForm 
        key={resetKey} 
        value={pointForm} 
        onChange={setPointForm} 
        dynamicSousTypes={dynamicSousTypes}      // ✅ remappé { code, labelFr }
        dynamicZones={dynamicZonesMapped}        // ✅ remappé { code, labelFr }
        resetForm={resetKey}
      />
      {/* 👇 CODE POINT LIVE - MASQUÉ*
      <div style={{ 
        marginBottom: "16px", 
        padding: "12px", 
        backgroundColor: isPointValid ? "#e8f5e8" : "#ffebee",
        borderRadius: "12px",
        borderLeft: `6px solid ${isPointValid ? "#4caf50" : "#f44336"}`,
        fontFamily: "monospace"
      }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "12px",
          flexWrap: "wrap"
        }}>
          <span style={{ fontSize: "11px", color: "#666" }}>
            Code point actuel :
          </span>
          <span style={{ 
            fontSize: "18px", 
            fontWeight: "700", 
            color: isPointValid ? "#2e7d32" : "#c62828",
            minWidth: "80px"
          }}>
            {isPointValid 
              ? PointCodeGenerator.generateCode(pointForm)
              : "Sélectionnez Famille + Résultat"
            }
          </span>
        </div>
      </div>
      /}

      {/* Modal score initial */}
      {showScoreInitModal && (
        <ScoreInitModal
          onConfirm={handleScoreInit}
          onClose={() => setShowScoreInitModal(false)}
          formatMatch={match.formatMatch || "BEST_OF_3"}
          premierServeur={match.premierServeur || "J1"}
        />
      )}

      {/* BOUTONS TACTILES */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "1fr 1fr", 
          gap: "16px",  
          marginBottom: "16px"
        }}>
          <button 
            onClick={() => addPointForPlayer("J1")}
            disabled={!isPointValid}
            style={{
              padding: "clamp(20px, 8vw, 28px) clamp(16px, 6vw, 24px)",
              background: "linear-gradient(135deg, #4caf50, #45a049)",
              color: "white",
              border: "none",
              borderRadius: "20px",
              fontSize: "clamp(16px, 5vw, 20px)",
              fontWeight: "700",
              boxShadow: "0 8px 24px rgba(76, 175, 80, 0.4)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              opacity: isPointValid ? 1 : 0.6,
              cursor: isPointValid ? "pointer" : "not-allowed"
            }}
          >
            <div>POINT</div>
            <div style={{ fontSize: "14px", fontWeight: "500" }}>
              {getPlayerDisplayName(match, "J1") || "J1"}
            </div>
          </button>
          <button 
            onClick={() => addPointForPlayer("J2")}
            disabled={!isPointValid}
            style={{
              padding: "clamp(20px, 8vw, 28px) clamp(16px, 6vw, 24px)",
              background: "linear-gradient(135deg, #f44336, #d32f2f)",
              color: "white",
              border: "none",
              borderRadius: "20px",
              fontSize: "clamp(16px, 5vw, 20px)",
              fontWeight: "700",
              boxShadow: "0 8px 24px rgba(244, 67, 54, 0.4)",
              minHeight: "72px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "2px",
              opacity: isPointValid ? 1 : 0.6,
              cursor: isPointValid ? "pointer" : "not-allowed"
            }}
          >
            <div>POINT</div>
            <div style={{ fontSize: "14px", fontWeight: "500" }}>
              {getPlayerDisplayName(match, "J2") || "J2"}
            </div>
          </button>
        </div>


        <button 
          onClick={undoPoint}
          style={{
            padding: "clamp(16px, 6vw, 20px) clamp(24px, 8vw, 32px)",
            background: "linear-gradient(135deg, #ff9800, #f57c00)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            fontSize: "clamp(14px, 4vw, 16px)",
            fontWeight: "600",
            width: "100%",
            boxShadow: "0 6px 20px rgba(255, 152, 0, 0.4)",
            cursor: "pointer",
            minHeight: "64px"            
          }}
        >
          ↶ Annuler ({pointsCount})
        </button>
        <button
          onClick={onBack}
          style={{
            padding: "clamp(16px, 6vw, 20px) clamp(24px, 8vw, 32px)",
            background: "linear-gradient(135deg, #2196f3, #1976d2)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            fontSize: "clamp(14px, 4vw, 16px)",
            fontWeight: "600",
            width: "100%",
            boxShadow: "0 6px 20px rgba(33, 150, 243, 0.4)",
            cursor: "pointer",
            minHeight: "64px",
            marginTop: "12px"
          }}
        >
          ← Liste matchs
        </button>
      
        {/* 🆕 BOUTON TERMINER LE MATCH */}
        {/* 🆕 MODAL DE FIN DE MATCH */}
        {showFinishModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '24px', color: '#333' }}>
                🏁 Terminer le match
              </h2>
              
              {/* Sélection de la raison */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '12px',
                  fontWeight: 'bold',
                  color: '#555'
                }}>
                  Raison :
                </label>
                
                <label style={{ display: 'block', marginBottom: '12px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="finishReason"
                    value="normal"
                    checked={finishReason === 'normal'}
                    onChange={(e) => {
                      setFinishReason(e.target.value);
                      setManualWinner(null);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  Match terminé normalement (utiliser le score)
                </label>
                
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="finishReason"
                    value="abandon"
                    checked={finishReason === 'abandon'}
                    onChange={(e) => setFinishReason(e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  Abandon d'un joueur
                </label>
              </div>
              
              {/* Sélection du vainqueur si abandon */}
              {finishReason === 'abandon' && (
                <div style={{ 
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#fff3e0',
                  borderRadius: '12px',
                  border: '2px solid #ff9800'
                }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '12px',
                    fontWeight: 'bold',
                    color: '#e65100'
                  }}>
                    Vainqueur par abandon :
                  </label>
                  
                  <label style={{ display: 'block', marginBottom: '12px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="manualWinner"
                      value="J1"
                      checked={manualWinner === 'J1'}
                      onChange={(e) => setManualWinner(e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    {getPlayerDisplayName(match, 'J1')}
                  </label>
                  
                  <label style={{ display: 'block', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="manualWinner"
                      value="J2"
                      checked={manualWinner === 'J2'}
                      onChange={(e) => setManualWinner(e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    {getPlayerDisplayName(match, 'J2')}
                  </label>
                </div>
              )}
              
              {/* Info */}
              <div style={{ 
                marginBottom: '24px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#666'
              }}>
                {pointsCount} points seront synchronisés
              </div>
              
              {/* Boutons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowFinishModal(false)}
                  style={{
                    flex: 1,
                    padding: '16px',
                    backgroundColor: '#e0e0e0',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: '#333'
                  }}
                >
                  Annuler
                </button>
                
                <button
                  onClick={() => {
                    // Validation
                    if (finishReason === 'abandon' && !manualWinner) {
                      alert('⚠️ Veuillez sélectionner le vainqueur');
                      return;
                    }
                    confirmFinishMatch(finishReason, manualWinner);
                  }}
                  disabled={finishReason === 'abandon' && !manualWinner}
                  style={{
                    flex: 1,
                    padding: '16px',
                    background: (finishReason === 'abandon' && !manualWinner) 
                      ? '#ccc' 
                      : 'linear-gradient(135deg, #4caf50, #388e3c)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: (finishReason === 'abandon' && !manualWinner) ? 'not-allowed' : 'pointer',
                    opacity: (finishReason === 'abandon' && !manualWinner) ? 0.6 : 1
                  }}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BOUTON TERMINER LE MATCH */}
        <button 
          onClick={handleFinishMatch}
          style={{
            padding: "clamp(16px, 6vw, 20px) clamp(24px, 8vw, 32px)",
            background: "linear-gradient(135deg, #4caf50, #388e3c)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            fontSize: "clamp(14px, 4vw, 16px)",
            fontWeight: "700",
            width: "100%",
            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
            cursor: "pointer",
            minHeight: "64px",
            marginTop: "12px"
          }}
        >
          🏁 Terminer le match
        </button>

        {/* 🆕 BOUTON PARTAGER LE MATCH */}
        <button 
          onClick={handleShare}
          style={{
            padding: "clamp(16px, 6vw, 20px) clamp(24px, 8vw, 32px)",
            background: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            fontSize: "clamp(14px, 4vw, 16px)",
            fontWeight: "700",
            width: "100%",
            boxShadow: "0 6px 20px rgba(156, 39, 176, 0.4)",
            cursor: "pointer",
            minHeight: "64px",
            marginTop: "12px"
          }}
        >
          📱 Partager le match
        </button>

        {/* 🔇 EXCEL EXPORT - Désactivé temporairement
        <button 
          onClick={() => ExcelExporter.exportCSVFeuillePrincipale(scoreEngine, match)}
          style={{
            padding: "clamp(16px, 6vw, 20px) clamp(24px, 8vw, 32px)",
            background: "linear-gradient(135deg, #4caf50, #388e3c)",
            color: "white",
            border: "none",
            borderRadius: "20px",
            fontSize: "clamp(14px, 4vw, 16px)",
            fontWeight: "700",
            width: "100%",
            boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
            cursor: "pointer",
            minHeight: "64px",
            marginTop: "12px"
          }}
        >
          📊 Excel Feuille
        </button>
        */}

        {/* Bouton Mode Expert - DEV uniquement */}
        {process.env.REACT_APP_ENV === 'development' && (
          <button 
            onClick={onTestZones}  // ← Plus simple : juste appeler la fonction !
            style={{
              padding: "clamp(16px, 6vw, 20px) clamp(24px, 8vw, 32px)",
              background: "linear-gradient(135deg, #ff9800, #f57c00)",
              color: "white",
              border: "none",
              borderRadius: "20px",
              fontSize: "clamp(14px, 4vw, 16px)",
              fontWeight: "700",
              width: "100%",
              boxShadow: "0 6px 20px rgba(255, 152, 0, 0.4)",
              cursor: "pointer",
              minHeight: "64px",
              marginTop: "12px"
            }}
          >
            🎯 Mode Expert
          </button>
        )}
      </div>
            {/* 👇 NOUVEAU : ZONE COMMENTAIRES */}
      <div style={{ 
        marginTop: "12px", 
        padding: "16px", 
        backgroundColor: "#f0f4f8", 
        borderRadius: "12px",
        border: "2px solid #e3f2fd"
      }}>
        <label style={{ 
          display: "block", 
          fontWeight: "600", 
          color: "#1976d2", 
          marginBottom: "8px",
          fontSize: "14px"
        }}>
          💬 Commentaires match
        </label>
        <textarea
          value={matchComments || ""}
          onChange={(e) => setMatchComments(e.target.value)}
          placeholder="Ex: 'Match intense, tie-break décisif set 3. J1 très solide au service.'"
          rows={3}
          style={{
            width: "100%",
            padding: "12px",
            border: "2px solid #bbdefb",
            borderRadius: "8px",
            fontSize: "14px",
            fontFamily: "inherit",
            resize: "vertical",
            minHeight: "80px",
            boxSizing: "border-box"
          }}
        />
        <div style={{ 
          fontSize: "12px", 
          color: "#666", 
          marginTop: "6px" 
        }}>
          {matchComments?.length || 0}/1000 caractères
        </div>
      </div>

      {/* 💬 Chat en temps réel avec les spectateurs */}
      {match && (
        <ChatPanel 
          matchId={match.id} 
          isScorer={true} 
          match={match}
        />
      )}
    </div>
  );
}

export default MatchLivePage;